import api from "../../../api";
import React from "react";
import utils from "../../../utils";
import values from "../../../values";
import Context from "../../../context";
import DriverScreen from "..";

import { TModal } from "../../../types/common.type";
import { useQuery } from "react-query";
import { DeleteModal } from "../../../components";
import {
  IBlockUser,
  IFindUserInformation,
  IPayAllDriverFactors,
  IPayDriverFactors,
  IUnBlockUser,
} from "../../../types/common.interface";
import {
  ICreateAndUpdateDriver,
  IDeposit,
} from "../../../types/driver.interface";

const CompanyDriverContainer = () => {
  // ** ====================================================================================================================
  // ** --------------------------------------------------------------------------------------------------------------------
  // **                                               Value Validations
  // ** --------------------------------------------------------------------------------------------------------------------
  const [showModal, setShowModal] = React.useState<TModal>("NONE");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedDriver, setSelectedDriver] = React.useState<any>();
  // ? Carrying last year's drivers into the selected year: the checkbox that also lists them, and the rows ticked
  // ? for transfer. Both are cleared by a successful transfer.
  const [includePreviousYear, setIncludePreviousYear] = React.useState<boolean>(false);
  const [selectedDriverIds, setSelectedDriverIds] = React.useState<string[]>([]);
  const [requestDriver, setRequestDriver] =
    React.useState<IFindUserInformation>({
      limit: values.constants.PANEL_TABLE_DEFAULT_LIMIT,
      page: 1,
    });
  // ** --------------------------------------------------------------------------------------------------------------------
  // ** ====================================================================================================================

  // !! ====================================================================================================================
  // !! --------------------------------------------------------------------------------------------------------------------
  // !!                                                Start API Cal
  // !! --------------------------------------------------------------------------------------------------------------------
  // ? Panel-wide academic year (سال تحصیلی). It has to be in the query key as well as the request, otherwise
  // ? react-query serves the previous year's cached rows when the year is switched.
  const { selectedYear } = React.useContext(Context.AcademicYear);

  const resultDriver = useQuery(
    [
      values.useQueryKeys.getAllSuperAgent,
      requestDriver?.page,
      requestDriver?.search?.value,
      requestDriver?.filters,
      selectedYear?._id,
      includePreviousYear,
    ],
    () =>
      api.post.company.findDriver({
        filters: requestDriver?.filters,
        search: requestDriver?.search,
        limit: requestDriver?.limit,
        page: requestDriver?.page,
        academicYear: selectedYear?._id,
        includePreviousYear,
      })
  );
  const { refetch } = resultDriver;

  // !! --------------------------------------------------------------------------------------------------------------------
  // !! ====================================================================================================================

  // ** ====================================================================================================================
  // ** --------------------------------------------------------------------------------------------------------------------
  // **                                               Local Functions
  // ** --------------------------------------------------------------------------------------------------------------------
  async function transferFunction(driverIds: string[]) {
    setIsLoading(true);
    const result = await api.post.common.transferDriversToAcademicYear({
      driverIds,
      academicYear: String(selectedYear?._id),
    });
    if (result) {
      utils.successHandler(Number(result.CODE));
      setSelectedDriverIds([]);
      refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }

  async function createFunction(formData: ICreateAndUpdateDriver) {
    setIsLoading(true);
    const result = await api.post.common.createDriverByAdmin(formData);
    if (result) {
      utils.successHandler(Number(result.CODE));
      resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function updateFunction(formData: ICreateAndUpdateDriver) {
    setIsLoading(true);
    const result = await api.put.common.updateDriverByAdmin(formData);
    if (result) {
      utils.successHandler(Number(result.CODE));
      resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function deleteFunction(userId: string) {
    setIsLoading(true);
    const result = await api.dele.common.deleteDriver(userId);
    if (result) {
      utils.successHandler(Number(result.CODE));
      resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function approveFunction(userId: string) {
    setIsLoading(true);
    const result = await api.put.common.approveDriver(userId);
    if (result) {
      utils.successHandler(Number(result.CODE));
      resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function blockFunction(props: IBlockUser) {
    setIsLoading(true);
    const result = await api.post.administrator.blockUser(props);
    if (result) {
      utils.successHandler(Number(result.CODE));
      resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function unBlockFunction(props: IUnBlockUser) {
    setIsLoading(true);
    const result = await api.dele.administrator.unBlock(props);
    if (result) {
      utils.successHandler(Number(result.CODE));
      resultDriver.refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function approveDocumentsFunction(driverId: string) {
    setIsLoading(true);
    const result = await api.put.administrator.approveDriverDocuments(driverId);
    if (result) {
      utils.successHandler(Number(2010));
      refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function payFunction(props: IPayAllDriverFactors) {
    setIsLoading(true);
    const result = await api.post.common.payAllDriverFactors(props);
    if (result) {
      utils.successHandler(Number(2010));
      refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }
  async function depositFunction(props: IDeposit) {
    console.log(props);
    setIsLoading(true);
    const result = await api.put.common.updateDriverDeposit(props);
    if (result) {
      utils.successHandler(Number(2010));
      refetch();
      setShowModal("NONE");
    }
    setIsLoading(false);
    return false;
  }

  // ** --------------------------------------------------------------------------------------------------------------------
  // ** ====================================================================================================================

  // ?? ====================================================================================================================
  // ?? --------------------------------------------------------------------------------------------------------------------
  // ??                                                JSX Container
  // ?? --------------------------------------------------------------------------------------------------------------------
  return (
    <Context.DriverScreen.Provider
      value={{
        showModal,
        isLoading,
        setShowModal,
        requestDriver,
        createFunction,
        updateFunction,
        deleteFunction,
        selectedDriver,
        approveFunction,
        setRequestDriver,
        setSelectedDriver,
        blockFunction,
        unBlockFunction,
        approveDocumentsFunction,
        payFunction,
        transferFunction,
        selectedDriverIds,
        setSelectedDriverIds,
        includePreviousYear,
        setIncludePreviousYear,
        depositFunction,
      }}
    >
      <DriverScreen resultDriver={resultDriver} role="COMPANY" />
      <DeleteModal
        data={selectedDriver}
        user={selectedDriver}
        open={showModal === "DELETE"}
        loading={isLoading}
        onClose={() => setShowModal("NONE")}
        onSubmit={deleteFunction}
      />
    </Context.DriverScreen.Provider>
  );

  // ?? --------------------------------------------------------------------------------------------------------------------
  // ?? ====================================================================================================================
};

export default CompanyDriverContainer;
